"use client";

import { useEffect, useRef, useState } from "react";
import {
  CountrySelectorDropdown,
  FlagImage,
  usePhoneInput,
  type ParsedCountry,
} from "react-international-phone";
import "react-international-phone/style.css";

type CountryPhoneInputProps = {
  id: string;
  name: string;
  value: string;
  onChange: (phone: string) => void;
  placeholder?: string;
  required?: boolean;
  compact?: boolean;
  variant?: "default" | "pill" | "underline" | "square";
  dropdownPlacement?: "top" | "bottom";
  className?: string;
};

const variantClasses = {
  default: {
    root: "rounded-xl border border-slate-200 bg-slate-50 focus-within:!border-slate-200 dark:border-slate-700 dark:bg-slate-800 dark:focus-within:!border-slate-700",
    input: "!rounded-r-xl",
    button: "!rounded-l-xl",
  },
  pill: {
    root: "rounded-full border-0 bg-white dark:bg-gray-800",
    input: "!rounded-r-full !px-2.5",
    button: "!rounded-l-full",
  },
  underline: {
    root: "!h-14 rounded-md border-0 border-b-2 border-gray-200 bg-transparent focus-within:!border-gray-200 dark:border-gray-700 dark:bg-transparent dark:focus-within:!border-gray-700",
    input: "!rounded-none !px-4",
    button: "!rounded-none !border-r-0",
  },
  square: {
    root: "rounded border-2 border-gray-200 bg-white focus-within:!border-gray-200 dark:border-gray-600 dark:bg-gray-800 dark:focus-within:!border-gray-600",
    input: "!rounded-r",
    button: "!rounded-l",
  },
} as const;

const selectorClassName =
  "!h-full !shrink-0 !cursor-pointer !rounded-r-none !border-0 !border-r !border-r-slate-200 !bg-transparent !px-1.5 !outline-none !ring-0 focus:!outline-none focus:!ring-0 focus-visible:!outline-none focus-visible:!ring-0 sm:!px-2 dark:!border-r-slate-700 dark:hover:!bg-slate-700";

const inputClassName =
  "!h-full !min-w-0 !flex-1 !border-0 !bg-transparent !font-open-sans !text-xs sm:!text-sm !text-slate-900 !outline-none !ring-0 placeholder:!font-open-sans placeholder:!text-xs sm:placeholder:!text-sm placeholder:!text-slate-400 focus:!border-0 focus:!outline-none focus:!ring-0 dark:!text-slate-100 dark:placeholder:!text-slate-500";

const dropdownClassName =
  "!static !z-auto !max-h-28 !w-full !cursor-pointer !overflow-x-hidden !overflow-y-auto !overscroll-contain !rounded-xl !border !border-slate-200 !bg-white !font-open-sans !shadow-xl !outline-none !ring-0 focus:!outline-none focus:!ring-0 dark:!border-slate-700 dark:!bg-slate-800";

const dropdownItemClasses = {
  listItemClassName:
    "!min-w-0 !cursor-pointer !text-sm !text-slate-700 hover:!bg-slate-100 dark:!text-slate-200 dark:hover:!bg-slate-700",
  listItemSelectedClassName: "!bg-blue-50 dark:!bg-blue-500/10",
  listItemFocusedClassName: "!bg-slate-100 dark:!bg-slate-700",
  listItemDialCodeClassName:
    "!ml-auto !shrink-0 !text-slate-500 dark:!text-slate-400",
  listItemCountryNameClassName: "!min-w-0 !truncate",
};

export default function CountryPhoneInput({
  id,
  name,
  value,
  onChange,
  placeholder = "Enter phone number",
  required = false,
  compact = false,
  variant = "default",
  dropdownPlacement = "bottom",
  className = "",
}: CountryPhoneInputProps) {
  const styles = variantClasses[variant];
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownId = `${id}-country-listbox`;

  // The hook (instead of <PhoneInput />) lets us skip the library's built-in
  // country selector, which always mounts its full hidden list. The list below
  // is only rendered while open.
  const { inputValue, handlePhoneValueChange, inputRef, country, setCountry } =
    usePhoneInput({
      defaultCountry: "in",
      disableDialCodePrefill: true,
      value,
      onChange: ({ phone }) => onChange(phone),
    });

  useEffect(() => {
    if (!isOpen) return;

    const handlePointerDown = (event: PointerEvent) => {
      if (!wrapperRef.current?.contains(event.target as Node)) setIsOpen(false);
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setIsOpen(false);
    };

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  const handleCountrySelect = (selected: ParsedCountry) => {
    setCountry(selected.iso2, { focusOnInput: true });
    setIsOpen(false);
  };

  return (
    <div ref={wrapperRef} className="relative w-full min-w-0">
      <div
        className={`flex w-full items-stretch font-open-sans outline-none ring-0 focus-within:!outline-none focus-within:!ring-0 transition ${compact ? "h-10 2xl:h-12" : "h-11 sm:h-12"} ${styles.root} ${className}`}
      >
        <button
          type="button"
          title={country.name}
          role="combobox"
          aria-label="Country selector"
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          aria-controls={isOpen ? dropdownId : undefined}
          className={`flex items-center gap-1 ${selectorClassName} ${styles.button}`}
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => setIsOpen((open) => !open)}
          onKeyDown={(event) => {
            if (["ArrowUp", "ArrowDown", "Enter", " "].includes(event.key)) {
              event.preventDefault();
              setIsOpen(true);
            }
          }}
        >
          <FlagImage iso2={country.iso2} />
          <span
            aria-hidden="true"
            className={`ml-1 size-0 border-x-4 border-t-4 border-x-transparent border-t-slate-500 transition-transform dark:border-t-slate-300 ${isOpen ? "rotate-180" : ""}`}
          />
        </button>

        <input
          ref={inputRef}
          id={id}
          name={name}
          type="tel"
          autoComplete="tel"
          required={required}
          placeholder={placeholder}
          value={inputValue}
          onChange={handlePhoneValueChange}
          className={`${inputClassName} ${compact ? "!px-1.5" : "!px-2.5"} ${styles.input}`}
        />
      </div>

      {isOpen ? (
        <div
          id={dropdownId}
          className={`absolute left-0 z-50 w-full ${
            dropdownPlacement === "top" ? "bottom-full mb-2" : "top-full mt-2"
          }`}
        >
          <CountrySelectorDropdown
            show
            selectedCountry={country.iso2}
            onSelect={handleCountrySelect}
            onClose={() => setIsOpen(false)}
            className={dropdownClassName}
            {...dropdownItemClasses}
          />
        </div>
      ) : null}
    </div>
  );
}
